import React from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList } from "react-window";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TrackTile from "./TrackTile";
import { perLine, collapsedLayout, expandedLayout, PITCH, STRIP_PAD } from "./tracksModel";

const SCROLLBAR = 8; // reserved in both states so tiles don't shift when a row expands

/**
 * A character's tracks. Collapsed: one line, as many tiles as fit, the last slot "+N more".
 * Expanded: every track wrapped to the same width, 4 lines tall, scrolling; only lines in view are drawn.
 */
export default function TrackStrip(props) {
  return (
    <AutoSizer disableHeight>
      {({ width }) => {
        if (!width) return null;
        const per = perLine(width - 2 * STRIP_PAD - SCROLLBAR);
        return props.expanded && props.tracks.length > per ? <Expanded {...props} per={per} width={width} /> : <Collapsed {...props} per={per} width={width} />;
      }}
    </AutoSizer>
  );
}

const tileProps = (p, track, index) => ({
  track,
  index,
  ids: p.ids,
  charId: p.charId,
  charName: p.charName,
  base: p.base,
  selected: p.sel.has(track.track_id),
  dragging: !!(p.dragging && p.dragging.has(track.track_id)),
  showMatch: p.showMatch,
  h: p.h,
  t: p.t,
});

function Collapsed(p) {
  const { shown, more } = collapsedLayout(p.tracks.length, p.per);
  // AutoSizer's wrapper is 0px wide, so the strip takes the measured width itself
  return (
    <div className="tk-strip" style={{ width: p.width }}>
      {p.tracks.slice(0, shown).map((track, i) => (
        <TrackTile key={track.track_id} {...tileProps(p, track, i)} />
      ))}
      {more > 0 && (
        <button
          type="button"
          className="tk-more"
          onClick={() => p.h.expand(p.charId)}
          aria-label={p.t("Show all {{count}} tracks", { count: p.tracks.length })}
        >
          +{more}
          <small>{p.t("More")}</small>
        </button>
      )}
    </div>
  );
}

function Expanded(p) {
  const { lines, height, lessIndex } = expandedLayout(p.tracks.length, p.per);
  return (
    <FixedSizeList
      className="tk-grid"
      width={p.width}
      height={height}
      itemCount={lines}
      itemSize={PITCH}
      overscanCount={1}
      itemData={{ ...p, lessIndex }}
    >
      {Line}
    </FixedSizeList>
  );
}

function Line({ index, style, data }) {
  const start = index * data.per;
  const end = Math.min(start + data.per, data.lessIndex + 1);
  const cells = [];
  for (let i = start; i < end; i++) {
    if (i === data.lessIndex)
      cells.push(
        <button key="less" type="button" className="tk-more" onClick={() => data.h.collapse(data.charId)} aria-label={data.t("Show less")}>
          <ExpandLessIcon />
          <small>{data.t("Less")}</small>
        </button>
      );
    else cells.push(<TrackTile key={data.tracks[i].track_id} {...tileProps(data, data.tracks[i], i)} />);
  }
  return (
    <div className="tk-line" style={style}>
      {cells}
    </div>
  );
}
